import React, { useEffect, useState } from 'react';
import NotificationTable from './NotificationTable';
import { fetchExternalUsers } from '../../services/external-user-service';
import { fetchRolesApi, fetchRolesByLoanApi } from '../../services/roles-service';
import { fetchFieldsByLoanId } from '../../services/fields-service';
import styles from './ContactsNotificationPreference.module.scss';
import Title from '../../../content/Title';
import SubordinateCompanySection from '../SubordinateCompany/SubordinateCompanySection';
import type { QueryExternalUsersOptions } from '../../models/ExternalUsers';
import { useMutation } from '@tanstack/react-query';
import type {LoanDataFields} from "../../models/LoanDataFields";
import Loader from '../Loader/Loader';
import type {Role} from '../../models/Role';

export interface ContactOption {
  id: string;
  disabledLogin?: boolean;
  displayName: string;
  email?: string;
  phone?: string;
  role?: Role[];
}

export interface ContactOptionMap {
  primary: ContactOption[];
  alternate: ContactOption[];
}

export interface ContactPreferenceProps {
  loanId?: string | null;
  userId?:string | null;
  authCode?:string | null;
}

const ContactsNotificationPreference: React.FC<ContactPreferenceProps> = ({ loanId,userId,authCode }) => {
  const [roleRules, setRoleRules] = useState<any>({});
  const [roleData, setRoleData] = useState<any>({});
  const [contactOptionMap, setContactOptionMap] = useState<ContactOptionMap>({
    primary: [],
    alternate: [],
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [loanType, setLoanType] = useState<string>('');
  const [fetchedTpoId, setFetchedTpoId] = useState<string>('');
  const [loanDataFields, setLoanDataFields] = useState<LoanDataFields>();//This can be used for accessing other fields
  const [hasFetchedRoles, setHasFetchedRoles] = useState<boolean>(false);
  const [hasFetchedUsers, setHasFetchedUsers] = useState<boolean>(false);

  const fetchFieldsMutation = useMutation({
    mutationFn: (loanId: string) => fetchFieldsByLoanId(loanId),
    onSuccess: (data) => {
      setLoanType(data.loanType === 'N' ? 'Non-Delegate' : 'Delegate');
      setFetchedTpoId(data.tpoId);
      setLoanDataFields(data); // Store the entire loan data fields
    },
    onError: (error) => {
      console.error('Error fetching fields:', error);
    }
  });

  const fetchRolesMutation = useMutation({
    mutationFn: fetchRolesApi,
    onSuccess: (roleRules) => {
      setRoleRules(
        roleRules['categorizedRoles'][
          loanType.includes('Non-Delegate') ? 'Non-Delegate-Loan' : 'Delegate-Loan'
          ]
      );
      setHasFetchedRoles(true);
    },
    onError: (error) => {
      console.error('Error fetching roles:', error);
      setRoleData(null);
    }
  });

  const fetchExternalUsersMutation = useMutation({
    mutationFn: (queryParam: QueryExternalUsersOptions) => fetchExternalUsers(queryParam),
    onSuccess: (users) => {
      if (Array.isArray(users)) {
        const newContacts = users.map((user) => ({
          id: user.id,
          disabledLogin: user.disabledLogin,
          displayName: user.firstName
            .concat(user.middleName ? ' ' + user.middleName + ' ' : ' ')
            .concat(user.lastName),
          email: user.email,
          phone: user.phone,
          role: user.roles,
        }));

        setContactOptionMap({
          primary: [...contactOptionMap.primary, ...newContacts],
          alternate: [...contactOptionMap.alternate, ...newContacts],
        });
        setHasFetchedUsers(true);
      } else {
        console.error('Expected an array of users, but got:', users);
      }
    },
    onError: (error) => {
      console.error('Error fetching users:', error);
    }
  });

  const fetchRolesByLoanMutation = useMutation({
    mutationFn: (loanId: string) => fetchRolesByLoanApi(loanId),
    onSuccess: (roles) => {
      const key = loanType.includes('Non-Delegate') ? 'Non-Delegate-Loan' : 'Delegate-Loan';
      if (roles && roles['categorizedRoles'] && roles['categorizedRoles'][key]) {
        setRoleData(roles['categorizedRoles'][key]);
      } else {
        const primaryTemplateKeys: string[] = Object.keys(roleRules);
        const templateObj: any = {};
        primaryTemplateKeys.forEach((key) => {
          templateObj[key] = roleRules[key].filter((obj: any) => obj['primary'] === true);
        });
        setRoleData(templateObj);
      }
    },
    onError: (error) => {
      console.error('Error fetching roles:', error);
    },
    onSettled: () => {
      setIsLoading(false);
    }
  });

  useEffect(() => {
    if (loanId) {
      fetchFieldsMutation.mutate(loanId);
    }
  }, [loanId]);

  useEffect(() => {
    if (loanType && !hasFetchedRoles) {
      fetchRolesMutation.mutate();
    }
  }, [loanType, hasFetchedRoles]);

  useEffect(() => {
    if (fetchedTpoId && !hasFetchedUsers) {
      const queryParam: QueryExternalUsersOptions = { tpoId: fetchedTpoId };
      fetchExternalUsersMutation.mutate(queryParam);
    }
  }, [fetchedTpoId, hasFetchedUsers]);

  useEffect(() => {
    if (loanId && Object.keys(roleRules).length) {
      fetchRolesByLoanMutation.mutate(loanId);
    }
  }, [loanId, roleRules]);
  return (
    <div className={styles.contactNotificationPage}>
      <Title
        title={'Loan Contacts & Notification Preferences'}
        descriptor={loanType === "Non-Delegate"? "" :"To add or change who we email about this loan, contact your company's administrator."}
      />
      {loanType === "Non-Delegate" && <div style={{    "marginBottom": "3rem"}}>
        <p style={{fontSize:"1.2rem",margin:0}}>Post Closing </p>
        <p>To add or change who we email about this loan, contact your company&#39;s administrator.</p>

      </div>}
      {isLoading ? <Loader /> : null}
      {roleData && loanType === "Non-Delegate" &&
        Object.keys(roleData)
          .sort()
          .reverse()
          .filter((key)=> key !== "Post Closing")
          .map((stage) => {
            const stageRoleData = roleData[stage];
            const stageRoleRules = roleRules[stage];

            if (!stageRoleData || !stageRoleRules) {
              return null;
            }

            return (
              <div key={loanId?.concat(stage)} className={styles.tableSection}>
                <NotificationTable
                  title={stage}
                  roleData={stageRoleData}
                  roleRules={stageRoleRules}
                  loanId={loanId}
                  loanType={loanType}
                  contactOptionMap={contactOptionMap}
                  loanDataFields={loanDataFields}
                />
              </div>
            );
          })}
      <SubordinateCompanySection
        tpoId={fetchedTpoId}
        loanId={loanId}
        loanDataFields={loanDataFields}
      />
    </div>
  );
};

export default ContactsNotificationPreference;
